import ReactSelect from 'components/base/ReactSelect';
import useDebounce from 'hooks/useDebounce';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import axiosInstance from 'utils/axiosInterceptors';
import { TProductDetails } from 'utils/types';
import { SelectOption } from './ProductSet';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import * as yup from 'yup';
import { AxiosError } from 'axios';

export type AddProductComponentFormProps = {
  productId: number | undefined;
  componentId?: number | undefined;
  quantity?: number;
  unitOfMeasure?: string;
};

export type AddProductComponentFormHandle = {
  submit: () => Promise<boolean | undefined>;
};

export type Component = {
  productId?: null | number;
  quantity: number;
  unitOfMeasure: string;
};

const AddProductComponentForm = forwardRef<
  AddProductComponentFormHandle,
  AddProductComponentFormProps
>(({ productId }, ref) => {
  const [products, setProducts] = useState<TProductDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [component, setComponent] = useState<Component>({
    productId: null,
    quantity: 0,
    unitOfMeasure: 'Piece'
  });
  const [input, setInput] = useState('');
  const debouncedInput = useDebounce(input, 500);

  const fetchData = async (search: string = '') => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(
        `/api/v1/products?page=1&pageSize=10&search=${search}`
      );
      setProducts(res.data.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const formattedProductOptions = products?.map(product => ({
    value: product.productId,
    label: product.name
  }));

  const handleFormChange = <K extends keyof Component>(
    field: K,
    value: Component[K]
  ) => {
    setComponent(prev => ({ ...prev, [field]: value }));
  };

  const componentSchema = yup.object().shape({
    productId: yup.number().required('Please select a product'),
    quantity: yup.number().required().min(1, 'Quantity must be at least 1')
  });

  const handleSubmit = async () => {
    try {
      await componentSchema.validate(component, { abortEarly: false });
      await axiosInstance.post(`/api/v1/productSets/${productId}/components`, {
        productId: component.productId,
        quantity: component.quantity,
        unitOfMeasure: component.unitOfMeasure
      });
      toast.success('Product added to the product set', {
        ...toastConfig,
        autoClose: 2000
      });
      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        error.inner.forEach(err => {
          toast.error(err.message, { ...toastConfig, autoClose: 3000 });
        });
      } else if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
      return false;
    }
  };

  useImperativeHandle(ref, () => ({
    submit: handleSubmit
  }));

  useEffect(() => {
    if (debouncedInput) {
      fetchData(debouncedInput);
    } else {
      fetchData();
    }
  }, [debouncedInput]);

  return (
    <>
      <Form className="w-100">
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Select Product</Form.Label>
            <ReactSelect
              placeholder="Select product set"
              options={formattedProductOptions}
              onChange={newValue =>
                handleFormChange('productId', (newValue as SelectOption).value)
              }
              isLoading={isLoading}
              closeMenuOnSelect={true}
              isSearchable
              onInputChange={newValue => setInput(newValue)}
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Quantity</Form.Label>
            <Form.Control
              type="number"
              value={component.quantity}
              onChange={e => handleFormChange('quantity', +e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col}>
            <Form.Label>Unit Of Measure</Form.Label>
            <Form.Control
              as="select"
              onChange={e => handleFormChange('unitOfMeasure', e.target.value)}
            >
              <option value="Piece">Piece</option>
              <option value="Set">Set</option>
              <option value="Meter">Meter</option>
              <option value="Foot">Foot</option>
            </Form.Control>
          </Form.Group>
        </Row>
      </Form>
    </>
  );
});

export default AddProductComponentForm;
